/**
 * THE WINDOW KEY AN MFE BUNDLE PUBLISHES ITSELF UNDER, composed once.
 *
 * `create-mfe-mount.tsx` writes `window.__federaMFE_<slug>` when a bundle finishes booting; `useMFEContainerMount.ts`
 * reads that same property to tell an already-loaded bundle from one still in flight, and both compose it here. A drift
 * between a publisher and a reader of the SAME key fails with no error signal at all: the
 * script loads, `script.onerror` never fires, the global is published under a name the host is not looking at, and the
 * host waits out `MFE_BUNDLE_LOAD_TIMEOUT_MS` and paints its error fallback over a working panel -- intermittently,
 * because a warm load takes the `isLoaded()` branch and never gets that far.
 *
 * BRAND-PREFIXED THROUGH `globalKey`, unlike the `mfe-<slug>-ready` DOM type its sibling `buildMfeReadyEventName`
 * (`const/mfe-names.ts`) composes. The asymmetry is deliberate and pinned by `__tests__/create-mfe-mount.test.tsx`: a
 * window key must be an identifier-ish property, while an event type is a plain kebab name on the wire.
 *
 * HOMED HERE rather than beside the two event composers in `const/mfe-names.ts`, which is where the rest of the
 * `mfe-<slug>` grammar lives: that leaf does not import `globalKey`, and the `const-add` flow adds a constant and its
 * TYPE imports only, so the value import this composition needs cannot be wired through the one door agents have into
 * `const/`. Both consumers sit in this package, so the reach is right even though the neighbourhood is not.
 *
 * @runtime-derived-ssot-helper: joins the brand prefix to the slug, so publisher and reader cannot spell it apart.
 */

import { globalKey } from '@federa/const/global-namespace';

/** `__federa` + `MFE_` + the slug, e.g. `buildMfeGlobalKey('navigator')` -> `'__federaMFE_navigator'`. */
export function buildMfeGlobalKey(slug: string): string {
	return `${globalKey('MFE_')}${slug}`;
}

/** The window key a host page publishes its MFE entry versions under -- `{ <slug>: <version> }`, written by
 *  `MfeEntryVersionsScript` and read by `resolveMfeEntryUrl` (iss-9d956529). One key for every slug, beside the
 *  per-slug bundle keys and never one of them. */
export const MFE_ENTRY_VERSIONS_GLOBAL_KEY = globalKey('MfeEntryVersions');
